/**
 * Client-Side Permission Hooks
 *
 * Provides React hooks for checking user permissions and rendering UI accordingly.
 * Uses BetterAuth admin plugin's client-side permission checking.
 *
 * @see openspec/changes/add-rbac-authorization/design.md
 */

import { useMemo } from "react"
import { authClient } from "@/lib/auth-client"
import {
  roleHasPermission,
  type Role,
  type Resource,
  type ActionsForResource,
} from "@/lib/permissions-client"

/**
 * Extended user type with RBAC fields
 */
export interface UserWithPermissions {
  id: string
  email: string
  name: string
  role: Role | null
  allowedCountryIds: string | null
  allowedStoreIds: string | null
}

/**
 * Permission check result
 */
export interface PermissionResult {
  allowed: boolean
  reason?: string
}

/**
 * Parse JSON array from string, returning empty array on error
 */
function parseJsonArray(value: string | null | undefined): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []
  } catch {
    return []
  }
}

/**
 * Main permissions hook - provides all permission-related functionality
 */
export function usePermissions() {
  const { data: session, isPending: isLoading } = authClient.useSession()

  const user = useMemo((): UserWithPermissions | null => {
    if (!session?.user) return null

    return {
      id: session.user.id,
      email: session.user.email,
      name: session.user.name,
      role: (session.user as unknown as { role?: string }).role as Role | null,
      allowedCountryIds:
        (session.user as unknown as { allowedCountryIds?: string }).allowedCountryIds ?? null,
      allowedStoreIds:
        (session.user as unknown as { allowedStoreIds?: string }).allowedStoreIds ?? null,
    }
  }, [session])

  const role = user?.role ?? null

  /**
   * Check if user has permission for a specific resource and action
   */
  const hasPermission = <R extends Resource>(
    resource: R,
    action: ActionsForResource<R>,
  ): PermissionResult => {
    if (!user) {
      return { allowed: false, reason: "Not authenticated" }
    }

    if (!role) {
      return { allowed: false, reason: "No role assigned" }
    }

    const allowed = roleHasPermission(role, resource, action)
    return {
      allowed,
      reason: allowed ? undefined : `Role "${role}" cannot ${action} ${resource}`,
    }
  }

  /**
   * Check if user has global access (no store restrictions)
   */
  const hasGlobalAccess = useMemo((): boolean => {
    if (!user) return false
    const countryIds = parseJsonArray(user.allowedCountryIds)
    const storeIds = parseJsonArray(user.allowedStoreIds)
    return countryIds.length === 0 && storeIds.length === 0
  }, [user])

  /**
   * Get user's store access restrictions
   */
  const storeAccess = useMemo(() => {
    if (!user) {
      return { countryIds: [], storeIds: [], isGlobal: false }
    }

    const countryIds = parseJsonArray(user.allowedCountryIds)
    const storeIds = parseJsonArray(user.allowedStoreIds)

    return {
      countryIds,
      storeIds,
      isGlobal: countryIds.length === 0 && storeIds.length === 0,
    }
  }, [user])

  /**
   * Check if user can access a specific store
   */
  const canAccessStore = (storeId: string, storeCountryId: string): boolean => {
    if (!user) return false
    if (hasGlobalAccess) return true

    const { countryIds, storeIds } = storeAccess

    // Store-level access takes precedence
    if (storeIds.length > 0) {
      return storeIds.includes(storeId)
    }

    // Country-level access
    if (countryIds.length > 0) {
      return countryIds.includes(storeCountryId)
    }

    return true // Global access
  }

  /**
   * Check if user is admin
   */
  const isAdmin = role === "admin"

  /**
   * Check if user has any elevated role (can manage users, etc.)
   */
  const canManageUsers = isAdmin

  return {
    user,
    role,
    isLoading,
    isAuthenticated: !!user,
    hasPermission,
    hasGlobalAccess,
    storeAccess,
    canAccessStore,
    isAdmin,
    canManageUsers,
  }
}

/**
 * Convenience hook: Check if user can create a resource
 */
export function useCanCreate(resource: Resource): PermissionResult {
  const { hasPermission, isLoading } = usePermissions()

  if (isLoading) {
    return { allowed: false, reason: "Loading permissions" }
  }

  return hasPermission(resource, "create" as ActionsForResource<typeof resource>)
}

/**
 * Convenience hook: Check if user can read a resource
 */
export function useCanRead(resource: Resource): PermissionResult {
  const { hasPermission, isLoading } = usePermissions()

  if (isLoading) {
    return { allowed: false, reason: "Loading permissions" }
  }

  return hasPermission(resource, "read" as ActionsForResource<typeof resource>)
}

/**
 * Convenience hook: Check if user can update a resource
 */
export function useCanUpdate(resource: Resource): PermissionResult {
  const { hasPermission, isLoading } = usePermissions()

  if (isLoading) {
    return { allowed: false, reason: "Loading permissions" }
  }

  return hasPermission(resource, "update" as ActionsForResource<typeof resource>)
}

/**
 * Convenience hook: Check if user can delete a resource
 */
export function useCanDelete(resource: Resource): PermissionResult {
  const { hasPermission, isLoading } = usePermissions()

  if (isLoading) {
    return { allowed: false, reason: "Loading permissions" }
  }

  return hasPermission(resource, "delete" as ActionsForResource<typeof resource>)
}

/**
 * Hook for route-level permission checking
 * Returns redirect path if user doesn't have access
 */
export function useRouteGuard(
  resource: Resource,
  action: ActionsForResource<typeof resource>,
): {
  isAllowed: boolean
  isLoading: boolean
  redirectPath: string | null
} {
  const { hasPermission, isLoading, isAuthenticated } = usePermissions()

  if (isLoading) {
    return { isAllowed: false, isLoading: true, redirectPath: null }
  }

  if (!isAuthenticated) {
    return { isAllowed: false, isLoading: false, redirectPath: "/login" }
  }

  const result = hasPermission(resource, action)

  if (!result.allowed) {
    return { isAllowed: false, isLoading: false, redirectPath: "/" }
  }

  return { isAllowed: true, isLoading: false, redirectPath: null }
}
